import type { Presence } from "../../domain/app-types.ts";

export interface PresenceCapabilities {
  selectedTaskId(): string | null;
  editing(): boolean;
  hidden(): boolean;
  actorId(): string;
  presence(): Presence[];
  setPresence(items: Presence[]): void;
  request(path: string, init?: RequestInit): Promise<Response | null>;
  refreshTask(taskId: string): Promise<void>;
  setInterval(callback: () => void, milliseconds: number): number;
  clearInterval(id: number): void;
}

export interface PresenceController { start(taskId: string): void; leave(taskId: string): Promise<void> }

export function createPresenceController(capabilities: PresenceCapabilities): PresenceController {
  let timer: number | null = null;
  let generation = 0;
  let activeTaskId: string | null = null;
  return {
    start(taskId) {
      if (timer !== null) capabilities.clearInterval(timer);
      const current = ++generation;
      let busy = false;
      const active = () => current === generation && capabilities.selectedTaskId() === taskId;
      const clear = async () => {
        if (!active() || !capabilities.presence().length) return;
        capabilities.setPresence([]);
        await capabilities.refreshTask(taskId);
      };
      // Never carry another task's presence into a new panel.
      if (activeTaskId !== taskId) {
        capabilities.setPresence([]);
        void capabilities.refreshTask(taskId).catch(() => undefined);
      }
      activeTaskId = taskId;
      const heartbeat = async () => {
        if (!active() || busy) return;
        busy = true;
        try {
          if (capabilities.hidden()) { await clear(); return; }
          const signal = AbortSignal.timeout(12_000);
          const response = await capabilities.request(`/api/v1/tasks/${taskId}/presence`, { signal, method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ actor_id: capabilities.actorId(), mode: capabilities.editing() ? "editing" : "viewing" }) });
          if (!active()) return;
          if (!response?.ok || capabilities.hidden()) { await clear(); return; }
          const listing = await capabilities.request(`/api/v1/tasks/${taskId}/presence`, { signal, credentials: "include" });
          if (!active()) return;
          if (!listing?.ok) { await clear(); return; }
          const next = await listing.json() as Presence[];
          if (!active()) return;
          if (capabilities.hidden()) { await clear(); return; }
          if (JSON.stringify(next) !== JSON.stringify(capabilities.presence())) { capabilities.setPresence(next); await capabilities.refreshTask(taskId); }
        } catch { await clear().catch(() => undefined); }
        finally { busy = false; }
      };
      void heartbeat(); timer = capabilities.setInterval(() => void heartbeat(), 20_000);
    },
    async leave(taskId) {
      // A late close for the old task must not stop a newer task's heartbeat.
      if (activeTaskId && activeTaskId !== taskId) {
        await capabilities.request(`/api/v1/tasks/${taskId}/presence`, { method: "DELETE", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ actor_id: capabilities.actorId(), mode: "viewing" }), keepalive: true }).catch(() => null);
        return;
      }
      generation++;
      activeTaskId = null;
      if (timer !== null) capabilities.clearInterval(timer); timer = null; capabilities.setPresence([]);
      await capabilities.request(`/api/v1/tasks/${taskId}/presence`, { method: "DELETE", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ actor_id: capabilities.actorId(), mode: "viewing" }), keepalive: true }).catch(() => null);
    },
  };
}
